import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { Global, css } from '@emotion/react';
import { Dropdown } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

import DownSvg from 'assets/svg/Down';
import { type AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { type KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';
import {
    createDefaultKpiAxisConfiguration,
    type KpiDataRange,
    type KpiBinLabelAffixes,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import AxisConfigDropdownPanel from 'components/molecules/PackArchitecture/AxisConfigDropdownPanel';
import AxisConfigSettingsModals from 'components/molecules/PackArchitecture/AxisConfigSettingsModals';
import { colorConstants } from 'config/colorConstants';
import { getKpiAxisConfigurationDefaultsFromMeta } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';

const AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS = 'pack-architecture-axis-config-dropdown';

const axisConfigDropdownGlobalStyles = css`
    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-dropdown-menu {
        padding: 0;
        background: transparent;
        box-shadow: none;
        border-radius: 0;
    }

    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .axis-dropdown-apply-btn.ant-btn-primary {
        background-color: ${colorConstants.violet} !important;
        border-color: ${colorConstants.violet} !important;
        color: ${colorConstants.white} !important;
    }

    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .axis-dropdown-apply-btn.ant-btn-primary:hover,
    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .axis-dropdown-apply-btn.ant-btn-primary:focus {
        background-color: ${colorConstants.purpleFontSelected} !important;
        border-color: ${colorConstants.purpleFontSelected} !important;
        color: ${colorConstants.white} !important;
    }

    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .axis-dropdown-apply-btn.ant-btn-primary:active {
        background-color: ${colorConstants.purpleFontPressed} !important;
        border-color: ${colorConstants.purpleFontPressed} !important;
        color: ${colorConstants.white} !important;
    }

    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-radio-wrapper > span:last-child {
        margin-left: 12px !important;
        padding: 0 4px !important;
        border-radius: 6px;
        background-color: transparent;
        transition: background-color 0.15s ease;
    }

    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-radio-wrapper:hover > span:last-child {
        background-color: ${colorConstants.radioLabelHoverBg};
    }

    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-radio-wrapper:active > span:last-child,
    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-radio-wrapper-checked > span:last-child,
    .${AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS} .ant-radio-wrapper-checked:hover > span:last-child {
        background-color: ${colorConstants.radioLabelPressedBg};
    }

    .pack-architecture-combined-bubble-config .ant-dropdown-trigger {
        display: flex;
        height: 32px;
        min-width: 0;
    }

    .pack-architecture-combined-bubble-config .ant-dropdown-trigger:last-of-type {
        width: auto;
        flex-shrink: 0;
    }
`;

const AxisConfigDropdownGlobalStyles: FC = () =>
    createElement(Global, { styles: axisConfigDropdownGlobalStyles });

export type AxisOption = {
    label: string;
    value: string;
    /** Optional unit affix from meta (e.g. "$"), shown on axis/table legends but not in the option label. */
    suffix?: string;
};

export type AxisOptionGroup = {
    title: string;
    options: AxisOption[];
};

const isDimensionAxisValue = (
    axisValue: string,
    groups: AxisOptionGroup[],
    kpiColumnOptionIds: string[],
) =>
    !kpiColumnOptionIds.includes(axisValue) &&
    groups.some((group) => group.options.some((option) => option.value === axisValue));

const isKpiAxisValue = (axisValue: string, kpiColumnOptionIds: string[]) =>
    kpiColumnOptionIds.includes(axisValue);

const getOptionLabel = (value: string, groups: AxisOptionGroup[]) => {
    for (const group of groups) {
        const match = group.options.find((option) => option.value === value);
        if (match) {
            return match.label;
        }
    }
    return value;
};

type AxisConfigDropdownProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    optionGroups?: AxisOptionGroup[];
    kpiColumnOptionIds?: string[];
    /** When true, renders without outer border — for use inside a combined toolbar group. */
    embedded?: boolean;
    showSettings?: boolean;
    /** Shows the settings affordance without opening Pack Architecture configuration modals. */
    showSettingsButton?: boolean;
    onSettingsClick?: () => void;
    onOpenChange?: (open: boolean) => void;
    hideGroupRestAsOther?: boolean;
    whitespacePreviewRequest?: Record<string, unknown> | null;
    useWhitespacePreview?: boolean;
    /** `column` for X-axis dimensions (default); `row` for Y-axis attribute-bucket dimensions (e.g. Pack_Range). */
    whitespacePreviewAxisKind?: 'column' | 'row';
    groupByControlsMeta?: AxisGroupByControlsMeta | null;
    kpiControlsMeta?: KpiAxisControlsMeta | null;
    chartRequest?: PricePackArchitectureRequest | null;
    apiTotalCount?: number;
    kpiDataRange?: KpiDataRange;
    kpiBinLabelAffixes?: KpiBinLabelAffixes;
    axisConfiguration?: AxisConfiguration;
    onAxisConfigurationChange?: (config: AxisConfiguration) => void;
    kpiAxisConfiguration?: KpiAxisConfiguration;
    onKpiAxisConfigurationChange?: (config: KpiAxisConfiguration) => void;
    triggerTestId?: string;
    settingsTestId?: string;
    isLoading?: boolean;
};

const AxisConfigDropdown = ({
    label,
    value,
    onChange,
    optionGroups = [],
    kpiColumnOptionIds = [],
    embedded = false,
    showSettings = false,
    showSettingsButton = false,
    onSettingsClick,
    onOpenChange,
    hideGroupRestAsOther = false,
    whitespacePreviewRequest = null,
    useWhitespacePreview = false,
    whitespacePreviewAxisKind = 'column',
    groupByControlsMeta = null,
    kpiControlsMeta = null,
    chartRequest = null,
    apiTotalCount = 0,
    kpiDataRange,
    kpiBinLabelAffixes,
    axisConfiguration,
    onAxisConfigurationChange,
    kpiAxisConfiguration,
    onKpiAxisConfigurationChange,
    triggerTestId,
    settingsTestId,
    isLoading = false,
}: AxisConfigDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [draftValue, setDraftValue] = useState(value);
    const [localAxisConfiguration, setLocalAxisConfiguration] = useState<AxisConfiguration | undefined>();
    const [localKpiAxisConfiguration, setLocalKpiAxisConfiguration] = useState<
        KpiAxisConfiguration | undefined
    >();

    const resolvedAxisConfiguration = axisConfiguration ?? localAxisConfiguration;
    const resolvedKpiAxisConfigurationState = kpiAxisConfiguration ?? localKpiAxisConfiguration;

    const selectedLabel = useMemo(() => getOptionLabel(value, optionGroups), [optionGroups, value]);
    const isDimensionAxis = useMemo(
        () => isDimensionAxisValue(value, optionGroups, kpiColumnOptionIds),
        [kpiColumnOptionIds, optionGroups, value],
    );
    const isKpiAxis = useMemo(
        () => isKpiAxisValue(value, kpiColumnOptionIds),
        [kpiColumnOptionIds, value],
    );
    const showAxisSettings =
        showSettingsButton || (showSettings && (isDimensionAxis || isKpiAxis));
    const kpiConfigurationDefaults = useMemo(
        () => getKpiAxisConfigurationDefaultsFromMeta(kpiControlsMeta),
        [kpiControlsMeta],
    );
    const resolvedKpiAxisConfiguration = useMemo(
        () =>
            resolvedKpiAxisConfigurationState ??
            createDefaultKpiAxisConfiguration(kpiDataRange ?? DEFAULT_KPI_DATA_RANGE, kpiConfigurationDefaults),
        [kpiConfigurationDefaults, kpiDataRange, resolvedKpiAxisConfigurationState],
    );

    const handleApplyAxisConfiguration = (config: AxisConfiguration) => {
        if (onAxisConfigurationChange) {
            onAxisConfigurationChange(config);
        } else {
            setLocalAxisConfiguration(config);
        }
    };

    const handleApplyKpiAxisConfiguration = (config: KpiAxisConfiguration) => {
        if (onKpiAxisConfigurationChange) {
            onKpiAxisConfigurationChange(config);
        } else {
            setLocalKpiAxisConfiguration(config);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setDraftValue(value);
        }
    }, [isOpen, value]);

    const handleApply = () => {
        onChange(draftValue);
        setIsOpen(false);
    };

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onOpenChange?.(open);
        },
        [onOpenChange],
    );

    const dropdownTrigger = (
        <Dropdown
            trigger={['click']}
            open={isOpen}
            onOpenChange={handleOpenChange}
            placement="bottomLeft"
            align={{ offset: [0, 5] }}
            overlayClassName={AXIS_CONFIG_DROPDOWN_OVERLAY_CLASS}
            dropdownRender={() => (
                <AxisConfigDropdownPanel
                    optionGroups={optionGroups}
                    draftValue={draftValue}
                    onDraftChange={setDraftValue}
                    onApply={handleApply}
                />
            )}
        >
            <button
                type="button"
                className={`flex min-w-0 cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left shadow-none outline-none ${
                    embedded ? 'h-8 px-3' : 'h-full min-w-0 flex-1 px-3'
                }`}
                data-testid={triggerTestId}
            >
                <span className="whitespace-nowrap text-[14px] text-toolbarLabelGrey">{label}:</span>
                <span
                    className="inline-flex min-w-0 items-center gap-2 text-sm font-normal"
                    style={{ color: colorConstants.blackTitle }}
                >
                    <span className="truncate">{selectedLabel}</span>
                    <DownSvg dropDownStroke={colorConstants.headerColor} />
                </span>
            </button>
        </Dropdown>
    );

    const settingsButton = showAxisSettings ? (
        <button
            type="button"
            disabled={isLoading}
            className={`relative -ml-px flex h-8 w-8 shrink-0 items-center justify-center rounded-r-md border border-borderGreyAl1 bg-white text-toolbarLabelGrey hover:z-10 hover:border-purpleFontSelected active:border-purpleFontPressed disabled:cursor-not-allowed disabled:opacity-50 ${
                isSettingsOpen ? 'z-10 !border-purpleFontPressed' : ''
            }`}
            aria-label={`${label} settings`}
            data-testid={settingsTestId}
            onClick={(event) => {
                event.stopPropagation();
                if (onSettingsClick) {
                    onSettingsClick();
                    return;
                }
                if (!showSettingsButton) {
                    setIsSettingsOpen(true);
                }
            }}
        >
            <SettingOutlined aria-hidden />
        </button>
    ) : null;

    const triggerShellClassName = showAxisSettings
        ? `relative flex h-8 min-w-0 flex-1 items-stretch overflow-hidden rounded-l-md border border-borderGreyAl1 hover:z-10 hover:border-purpleFontSelected active:border-purpleFontPressed ${
              isOpen ? 'z-10 !border-purpleFontPressed' : ''
          }`
        : `flex h-8 items-stretch overflow-hidden rounded-md border border-borderGreyAl1 hover:border-purpleFontSelected active:border-purpleFontPressed ${
              isOpen ? '!border-purpleFontPressed' : ''
          }`;

    return (
        <>
            <AxisConfigDropdownGlobalStyles />
            {embedded ? (
                dropdownTrigger
            ) : showAxisSettings ? (
                <div className="flex h-8 items-stretch">
                    <div className={triggerShellClassName}>{dropdownTrigger}</div>
                    {settingsButton}
                </div>
            ) : (
                <div className={triggerShellClassName}>{dropdownTrigger}</div>
            )}
            <AxisConfigSettingsModals
                isDimensionAxis={isDimensionAxis}
                isKpiAxis={isKpiAxis}
                isSettingsOpen={isSettingsOpen}
                selectedLabel={selectedLabel}
                xAxis={value}
                chartRequest={chartRequest}
                apiTotalCount={apiTotalCount}
                groupByControlsMeta={groupByControlsMeta}
                kpiControlsMeta={kpiControlsMeta}
                axisConfiguration={resolvedAxisConfiguration}
                resolvedKpiAxisConfiguration={resolvedKpiAxisConfiguration}
                dataRange={kpiDataRange ?? DEFAULT_KPI_DATA_RANGE}
                kpiBinLabelAffixes={kpiBinLabelAffixes}
                hideGroupRestAsOther={hideGroupRestAsOther}
                whitespacePreviewRequest={whitespacePreviewRequest}
                useWhitespacePreview={useWhitespacePreview}
                whitespacePreviewAxisKind={whitespacePreviewAxisKind}
                onClose={() => setIsSettingsOpen(false)}
                onApplyAxisConfiguration={handleApplyAxisConfiguration}
                onApplyKpiAxisConfiguration={handleApplyKpiAxisConfiguration}
            />
        </>
    );
};

export default AxisConfigDropdown;
